import * as React from "react";

import cn from "classnames";
import IconMail from "../Icons/IconMail";

import styles from "./EmailField.module.scss";

/**
 * Test an email string in 5 parts
 * * 1 - 64 characters: letters numbers dot underscore hyphen percent plus
 * * followed by exactly one '@' symbol
 * * 3 - 63 characters: letters numbers hyphen
 * * followed by exactly one dot
 * * at least 2 characters: letters dashes dots
 */
const emailRegExp = /^([\w._%+-]{1,64})+([@])+([a-z0-9-]{3,63})+([.])+[a-z-.]{2,}$/i;


/**
 * Validates an email filed - xx@xx.xx.
 * * No empty strings
 * * No white space characters.
 * * Entire string is no longer than 256 characters.
 * * Name portion is 1 - 64 characters: letters numbers dot underscore hyphen percent plus
 * * Followed by exactly one '@' symbol
 * * Domain portion is 3 - 63 characters: letters numbers hyphen
 * * Followed by exactly one dot
 * * Top Level domain is at least 2 characters: letters dashes dots
 * * * No consecutive dots
 * * * Does not begin with a dot
 * * * Does not end with a dot.
 */
const validateEmail = (email: string): boolean => {
    const nameReg = /^[a-z0-9_.%+-]+$/i;
    const domainReg = /^[a-z0-9-]+$/i;
    const tldRegex = /^[a-z-.]+$/i;
    const [username, site] = email.split("@");
    const dot = ".";

    // Check for empty string or length exceeding 256 characters
    if (email === "" || email.length > 256) {
        return false;
    }

    // Check for presence of '@' symbol and correct number of parts
    if (username === undefined || site === undefined) {
        return false;
    }

    // Validate username
    if (!username.length || username.length > 64 || !nameReg.test(username)) {
        return false;
    }

    // Split domain into domain name and top-level domain (TLD)
    const [domain, ...rest] = site.split(dot);
    const tld = rest.join(dot);

    // Validate domain name
    if (!domain.length || domain.length > 63 || !domainReg.test(domain) || domain.startsWith(dot) || domain.endsWith(dot)) {
        return false;
    }

    // Validate top-level domain (TLD)
    if (tld.length < 2 || !tldRegex.test(tld) || tld.startsWith(dot) || tld.endsWith(dot)) {
        return false;
    }

    return true;
};

interface FieldWrapperProps {
    /**
     * Any errors that should be displayed below the field.
     */
    fieldErrors?: string[];
    label?: string;
    id?: string;
    className?: string;
    inputClassName?: string;
    outlineClassName?: string;
    errorClassName?: string;
    errorMsgClassName?: string;
    labelClassName?: string;
    withIcon?: boolean;
}

interface BaseFieldProps<T extends HTMLElement> extends FieldWrapperProps, Partial<React.HTMLProps<T>> {}

/**
 * Generates an email field, including label and errors.
 */
const EmailField = (props: BaseFieldProps<HTMLInputElement>): React.ReactElement => {
    const {
        id,
        label,
        className,
        inputClassName,
        outlineClassName,
        errorClassName,
        errorMsgClassName,
        labelClassName,
        withIcon,
        fieldErrors = [],
        required = false,
        ...inputProps
    } = props;

    const hasErrors = fieldErrors.length > 0;
    const labelClassNames = cn(styles.label, labelClassName, hasErrors ? styles.labelError : "");

    const inputGroupClassNames = cn(
        styles.inputGroup,
        hasErrors ? cn(styles.groupError, errorClassName) : "",
        withIcon ? styles.withIcon : ""
    );

    return (
        <span className={cn(styles.field, className)}>
            <label htmlFor={id} className={labelClassNames}>
                {label}{required ? "*" : ""}
            </label>
            <span className={inputGroupClassNames}>
                <input
                    className={cn(styles.input, inputClassName)}
                    id={id}
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    type="email"
                    autoComplete="email"
                    aria-required={required}
                    aria-describedby="emailErrors"
                    required={required}
                    aria-label={hasErrors ? fieldErrors.join(" ") : label}
                    pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                    {...inputProps}
                />
                <span className={cn(styles.outline, outlineClassName)}>
                    {withIcon && <IconMail className={styles.icon} />}
                </span>
            </span>
            { hasErrors && <span id="emailErrors">
                { fieldErrors.map((error) => (
                <span key={error} className={cn(styles.error, styles.fieldError, errorMsgClassName)}>
                    Error: {error}
                </span>
                ))}
            </span> }
        </span>
    );
}

export default EmailField;
export { validateEmail, emailRegExp, EmailField };
export type { FieldWrapperProps, BaseFieldProps };
